import { IExcelReportData, ITableData } from 'types/excelExport';
import {
    createBlankRow,
    createCashFlowRow,
    createDarkSectionHeader,
    createDataRow,
    createMonthlyHeaderRow,
    parseNumber,
} from '../helpers';
import { EXCEL_COLORS } from '../constants';
import { LD_LABELS } from './constants';
import { ILdCashFlowSheetParams } from './types';

export const formatLdCashFlowSheet = ({
    dealName,
    returns,
    showCents = false,
}: ILdCashFlowSheetParams): IExcelReportData => {
    const labels = LD_LABELS.cashFlow;
    const today = new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

    const returnsSource = returns.cashFlow ?? [];
    const saleIndex = returnsSource.findIndex(cf => {
        const value = parseNumber(cf.netSaleProceeds);
        return value !== null && value !== 0;
    });
    const returnsCashFlows = saleIndex === -1
        ? returnsSource
        : returnsSource.slice(0, saleIndex + 1);
    const totalMonths = returnsCashFlows.length;
    const colCount = totalMonths + 2; // monthly columns + Total column

    const topHeaderRows: ITableData[] = [
        createDataRow([{ value: dealName, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: labels.title, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: today, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createBlankRow(colCount),
        createMonthlyHeaderRow(returnsCashFlows, true),
    ];

    const developmentBudgetRows: ITableData[] = [
        createDarkSectionHeader(labels.developmentBudget, colCount),
        createCashFlowRow(
            labels.totalDevelopmentCosts,
            returnsCashFlows,
            cf => -(parseNumber(cf.projectCost) ?? 0),
            showCents,
            true,
            false,
            EXCEL_COLORS.GRAY_HEADER,
            undefined,
            true,
        ),
    ];

    const constructionFinancingRows: ITableData[] = [
        createBlankRow(colCount),
        createDarkSectionHeader(labels.constructionFinancing, colCount),
        createCashFlowRow(
            labels.loanDraws,
            returnsCashFlows,
            cf => cf.constructionLoanDraws,
            showCents,
            false,
            false,
            undefined,
            undefined,
            true,
        ),
        createCashFlowRow(
            labels.interest,
            returnsCashFlows,
            cf => -(parseNumber(cf.constructionLoanInterest) ?? 0),
            showCents,
            false,
            false,
            undefined,
            undefined,
            true,
        ),
        createCashFlowRow(
            labels.financingFees,
            returnsCashFlows,
            cf => -(parseNumber(cf.financingFee) ?? 0),
            showCents,
            false,
            false,
            undefined,
            undefined,
            true,
        ),
        createCashFlowRow(
            labels.constructionLoanPayoff,
            returnsCashFlows,
            cf => cf.constructionLoanPayoff,
            showCents,
            false,
            false,
            undefined,
            undefined,
            true,
        ),
    ];

    const saleProceedsRows: ITableData[] = [
        createBlankRow(colCount),
        createDarkSectionHeader(labels.saleProceeds, colCount),
        createCashFlowRow(
            labels.netSaleProceeds,
            returnsCashFlows,
            cf => cf.netSaleProceeds,
            showCents,
            true,
            false,
            EXCEL_COLORS.GRAY_HEADER,
            undefined,
            true,
        ),
    ];

    const netLeveredCashFlowRows: ITableData[] = [
        createBlankRow(colCount),
        createCashFlowRow(
            labels.netLeveredCashFlow,
            returnsCashFlows,
            cf => cf.netLeveredCashFlow,
            showCents,
            true,
            false,
            EXCEL_COLORS.GRAY_HEADER,
            EXCEL_COLORS.DARK_GRAY,
            true,
        ),
    ];

    return {
        topHeaderRows,
        reportTables: [
            {
                tableHeaderRows: [],
                tableData: [
                    ...developmentBudgetRows,
                    ...constructionFinancingRows,
                    ...saleProceedsRows,
                    ...netLeveredCashFlowRows,
                ],
            },
        ],
    };
};
